import React, { useEffect, useMemo, useState } from 'react';
import Modal from './Modal.mjs';
import ClosingX from './ClosingX.jsx';
import TagSelector from './TagSelector.jsx';
import Chip from './common/Chip.jsx';
import EmptyState from './common/EmptyState.jsx';
import {
    addTagToFile,
    removeTagFromFile,
    createTag,
    getAllAvailableTags,
} from '../services/TagService.mjs';
import { runBulk } from '../utils/bulkApply';
import { notify, reportError } from '../notifications/errorBus';
import '../css/BulkActions.css';

const SKIP = { skipGlobalErrorToast: true };

/**
 * Add or remove a tag across every selected file.
 *
 * `files` are the selected file documents (each with `id` and `metadata.tags`).
 * `onApplied` refreshes the underlying grid (and clears the selection) after a
 * successful mutation; we close the modal at the same time because that refresh
 * makes the snapshot in `files` stale.
 */
const BulkTagModal = ({ files, onClose, onApplied }) => {
    const [tab, setTab] = useState('add');
    const [allTags, setAllTags] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        getAllAvailableTags()
            .then(tags => setAllTags(Array.isArray(tags) ? tags : []))
            .catch(() => setAllTags([]));
    }, []);

    const tagsById = useMemo(
        () => Object.fromEntries(allTags.map(t => [t.id, t.name])),
        [allTags]
    );

    // Tags currently present on the selection, with how many files carry each.
    const presentTags = useMemo(() => {
        const counts = new Map();
        for (const f of files) {
            for (const tagId of (f.metadata?.tags || [])) {
                counts.set(tagId, (counts.get(tagId) || 0) + 1);
            }
        }
        return Array.from(counts.entries())
            .map(([id, count]) => ({ id, count, name: tagsById[id] || id }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [files, tagsById]);

    const finish = (verb, succeeded, failed) => {
        if (succeeded > 0) {
            notify({
                type: 'success',
                title: `Tag ${verb}`,
                message: `${verb === 'added' ? 'Added to' : 'Removed from'} ${succeeded} file(s).`,
            });
        }
        if (failed > 0) {
            reportError(
                `${failed} file(s) could not be updated. Please try again.`,
                { title: `Some files could not be updated` }
            );
        }
        onApplied?.();
        onClose?.();
    };

    const applyAdd = async (tagId) => {
        if (!tagId) return;
        setBusy(true);
        try {
            // Skip files that already carry the tag — nothing to do, and it
            // keeps the success count honest.
            const targets = files.filter(f => !(f.metadata?.tags || []).includes(tagId));
            if (targets.length === 0) {
                notify({ type: 'info', title: 'Already tagged', message: 'Every selected file already has that tag.' });
                onClose?.();
                return;
            }
            const { succeeded, failed } = await runBulk(targets, f => addTagToFile(f.id, tagId, SKIP));
            finish('added', succeeded.length, failed.length);
        } finally {
            setBusy(false);
        }
    };

    const handleExistingTagAdded = (tagId) => applyAdd(tagId);

    const handleNewTagAdded = async (tagName) => {
        setBusy(true);
        try {
            const tagId = await createTag(tagName);
            await applyAdd(tagId);
        } catch (err) {
            reportError(err, { title: 'Could not create tag' });
        } finally {
            setBusy(false);
        }
    };

    const handleRemove = async (tagId) => {
        setBusy(true);
        try {
            const targets = files.filter(f => (f.metadata?.tags || []).includes(tagId));
            const { succeeded, failed } = await runBulk(targets, f => removeTagFromFile(f.id, tagId, SKIP));
            finish('removed', succeeded.length, failed.length);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal onClose={onClose}>
            <div className="bulk-modal">
                <div className="bulk-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <h2>Tags</h2>
                        <p>{files.length} file(s) selected</p>
                    </div>
                    <ClosingX onClick={onClose} />
                </div>

                <div className="bulk-tabs">
                    <button className={`bulk-tab ${tab === 'add' ? 'active' : ''}`} onClick={() => setTab('add')}>
                        Add tag
                    </button>
                    <button className={`bulk-tab ${tab === 'remove' ? 'active' : ''}`} onClick={() => setTab('remove')}>
                        Remove tag
                    </button>
                </div>

                {tab === 'add' ? (
                    <TagSelector
                        initialTags={[]}
                        onExistingTagAdded={handleExistingTagAdded}
                        onNewTagAdded={handleNewTagAdded}
                    />
                ) : (
                    presentTags.length > 0 ? (
                        <div className="bulk-pill-list">
                            {presentTags.map(t => (
                                <Chip
                                    key={t.id}
                                    variant="neutral"
                                    onRemove={busy ? undefined : () => handleRemove(t.id)}
                                    removeLabel={`Remove "${t.name}" from ${t.count} file(s)`}
                                >
                                    {t.name}
                                    <span className="bulk-pill-count">{t.count}/{files.length}</span>
                                </Chip>
                            ))}
                        </div>
                    ) : (
                        <EmptyState compact title="No tags to remove" description="None of the selected files have tags." />
                    )
                )}
            </div>
        </Modal>
    );
};

export default BulkTagModal;
